import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CatalogFacets, Category, Product, ProductQuery } from '@skytech/shared';

import { api } from '../../lib/api';

/** Serialises a ProductQuery into a stable querystring for the API + cache key. */
function toSearchParams(query: ProductQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.category) params.set('category', query.category);
  query.brand?.forEach((b) => params.append('brand', b));
  if (query.minPrice !== undefined) params.set('minPrice', String(query.minPrice));
  if (query.maxPrice !== undefined) params.set('maxPrice', String(query.maxPrice));
  if (query.inStock) params.set('inStock', 'true');
  if (query.minRating !== undefined) params.set('minRating', String(query.minRating));
  if (query.sort) params.set('sort', query.sort);
  if (query.limit) params.set('limit', String(query.limit));
  return params.toString();
}

export const productKeys = {
  all: ['products'] as const,
  list: (q: ProductQuery) => ['products', 'list', q] as const,
  detail: (idOrSlug: string) => ['products', 'detail', idOrSlug] as const,
  autocomplete: (term: string) => ['products', 'autocomplete', term] as const,
};

/** Cursor-paginated product list. Backs both the catalog grid and "Load more". */
export function useProducts(query: ProductQuery) {
  return useInfiniteQuery({
    queryKey: productKeys.list(query),
    staleTime: 5 * 60_000,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const search = toSearchParams(query);
      const cursor = pageParam ? `&cursor=${pageParam}` : '';
      return api.get<Product[]>(`/products?${search}${cursor}`);
    },
    getNextPageParam: (lastPage) => lastPage.meta?.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useProduct(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: productKeys.detail(idOrSlug ?? ''),
    staleTime: 5 * 60_000,
    enabled: Boolean(idOrSlug),
    queryFn: async () => (await api.get<Product>(`/products/${idOrSlug}`)).data,
  });
}

/** Homepage "Featured" rail — admin-curated products (fallback top-rated). */
export function useFeaturedProducts() {
  return useQuery({
    queryKey: ['products', 'featured'],
    queryFn: async () => (await api.get<Product[]>('/products/featured')).data,
    staleTime: 60_000,
  });
}

/** Cross-sell: admin-curated related products (fallback same-category) for PDP + cart. */
export function useRelated(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: ['products', 'related', idOrSlug ?? ''],
    enabled: Boolean(idOrSlug),
    queryFn: async () => (await api.get<Product[]>(`/products/${idOrSlug}/related`)).data,
    staleTime: 60_000,
  });
}

/** Full-text search results (ts_rank ordered). */
export function useSearch(query: ProductQuery, enabled = true) {
  return useQuery({
    queryKey: ['products', 'search', query],
    enabled: enabled && Boolean(query.q),
    queryFn: async () => api.get<Product[]>(`/products/search?${toSearchParams(query)}`),
    placeholderData: keepPreviousData,
  });
}

export interface AutocompleteHit {
  id: string;
  name: string;
  slug: string;
  similarity: number;
}

/** Fuzzy, typo-tolerant suggestions for the search box (pg_trgm). */
export function useAutocomplete(term: string) {
  return useQuery({
    queryKey: productKeys.autocomplete(term),
    enabled: term.trim().length >= 2,
    queryFn: async () =>
      (await api.get<AutocompleteHit[]>(`/products/autocomplete?q=${encodeURIComponent(term)}`))
        .data,
    staleTime: 30_000,
  });
}

/** Live price range + brand counts for the filter sidebar, scoped to the active category. */
export function useCatalogFacets(category?: string) {
  return useQuery({
    queryKey: ['products', 'facets', category ?? 'all'],
    queryFn: async () =>
      (
        await api.get<CatalogFacets>(
          `/products/facets${category ? `?category=${encodeURIComponent(category)}` : ''}`,
        )
      ).data,
    staleTime: 5 * 60_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories', 'tree'],
    queryFn: async () => (await api.get<Category[]>('/categories')).data,
    staleTime: 5 * 60_000,
  });
}

/** Lean card shape for the homepage "Shop by category" carousel (§4.2). */
export interface FeaturedCategory {
  id: string;
  name: string;
  slug: string;
  thumbnail: string | null;
  productCount: number;
}

/** Admin-curated categories for the homepage carousel (falls back to top-level). */
export function useFeaturedCategories() {
  return useQuery({
    queryKey: ['categories', 'featured'],
    queryFn: async () => (await api.get<FeaturedCategory[]>('/categories/featured')).data,
    staleTime: 5 * 60_000,
  });
}
